import type { NotificationEventType } from "@common/client"
import {
  AtSign,
  CalendarClock,
  ClipboardList,
  FileText,
  Hammer,
  type LucideIcon,
  Mail,
  NotebookPen,
  Package,
  ReceiptText,
  Stamp,
  TriangleAlert,
} from "lucide-react"

export const EVENT_AREAS = [
  "approvals",
  "mentions",
  "squawks",
  "work_orders",
  "parts",
  "programs",
  "quotes",
  "tech_logs",
  "tools",
  "documents",
  "emails",
] as const

export type EventArea = (typeof EVENT_AREAS)[number]

export const AREAS: Record<EventArea, { label: string; icon: LucideIcon }> = {
  approvals: { label: "Approvals", icon: Stamp },
  mentions: { label: "Mentions", icon: AtSign },
  squawks: { label: "Squawks", icon: TriangleAlert },
  work_orders: { label: "Work orders", icon: ClipboardList },
  parts: { label: "Parts and services", icon: Package },
  programs: { label: "Maintenance programs", icon: CalendarClock },
  quotes: { label: "Quotes", icon: ReceiptText },
  tech_logs: { label: "Tech logs", icon: NotebookPen },
  tools: { label: "Tools", icon: Hammer },
  documents: { label: "Documents", icon: FileText },
  emails: { label: "Emails", icon: Mail },
}

export const EVENTS: Record<
  NotificationEventType,
  { label: string; area: EventArea }
> = {
  approval_requested: { label: "An approval is waiting", area: "approvals" },
  mentioned: { label: "Someone is tagged", area: "mentions" },
  squawk_created: { label: "Squawk logged", area: "squawks" },
  squawk_status_changed: { label: "Squawk status changed", area: "squawks" },
  squawk_moved: { label: "Squawk moved", area: "squawks" },
  squawk_comment_added: { label: "Squawk comment added", area: "squawks" },
  work_order_created: { label: "Work order created", area: "work_orders" },
  work_order_details_updated: {
    label: "Work order details changed",
    area: "work_orders",
  },
  work_order_status_changed: {
    label: "Work order status changed",
    area: "work_orders",
  },
  work_order_aog_marked: {
    label: "Work order marked AOG",
    area: "work_orders",
  },
  work_order_aog_cleared: { label: "AOG cleared", area: "work_orders" },
  work_order_tasks_added: {
    label: "Tasks added to a work order",
    area: "work_orders",
  },
  work_order_tasks_removed: {
    label: "Tasks removed from a work order",
    area: "work_orders",
  },
  work_task_statuses_changed: {
    label: "Task status changed",
    area: "work_orders",
  },
  work_tasks_assigned: { label: "Tasks assigned", area: "work_orders" },
  work_task_signed_off: { label: "Task signed off", area: "work_orders" },
  work_task_sign_off_undone: {
    label: "Task sign-off undone",
    area: "work_orders",
  },
  work_task_inspected: { label: "Task inspected", area: "work_orders" },
  work_task_inspection_undone: {
    label: "Task inspection undone",
    area: "work_orders",
  },
  work_task_note_added: { label: "Task note added", area: "work_orders" },
  work_task_part_requested: {
    label: "Part requested for a task",
    area: "parts",
  },
  work_task_part_withdrawn: { label: "Part request withdrawn", area: "parts" },
  work_task_part_issued: { label: "Part issued to a task", area: "parts" },
  part_reserved: { label: "Part reserved", area: "parts" },
  part_reservation_changed: {
    label: "Part reservation changed",
    area: "parts",
  },
  part_reservation_released: {
    label: "Part reservation released",
    area: "parts",
  },
  part_stock_low: { label: "Part stock low", area: "parts" },
  outside_service_requested: {
    label: "Outside service requested",
    area: "parts",
  },
  outside_service_moved: { label: "Outside service moved on", area: "parts" },
  outside_service_withdrawn: {
    label: "Outside service withdrawn",
    area: "parts",
  },
  programme_created: { label: "Program created", area: "programs" },
  programme_archived: { label: "Program archived", area: "programs" },
  programme_restored: { label: "Program restored", area: "programs" },
  programme_updated: { label: "Program details changed", area: "programs" },
  programme_revision_created: {
    label: "Program revision created",
    area: "programs",
  },
  programme_revision_updated: {
    label: "Program revision details changed",
    area: "programs",
  },
  programme_revision_released: {
    label: "Program revision released",
    area: "programs",
  },
  programme_task_added: { label: "Program task added", area: "programs" },
  programme_task_updated: { label: "Program task changed", area: "programs" },
  programme_task_removed: { label: "Program task removed", area: "programs" },
  programme_phase_added: { label: "Program phase added", area: "programs" },
  programme_phase_updated: { label: "Program phase changed", area: "programs" },
  programme_phase_removed: { label: "Program phase removed", area: "programs" },
  programme_aircraft_enrolled: {
    label: "Aircraft enrolled in a program",
    area: "programs",
  },
  programme_aircraft_removed: {
    label: "Aircraft removed from a program",
    area: "programs",
  },
  customer_responded: { label: "Customer responded", area: "quotes" },
  comment_added: { label: "Quote comment added", area: "quotes" },
  review_requested: { label: "Quote review requested", area: "quotes" },
  quote_created: { label: "Quote created", area: "quotes" },
  quote_details_updated: { label: "Quote details changed", area: "quotes" },
  quote_tasks_added: { label: "Tasks added to a quote", area: "quotes" },
  quote_tasks_removed: { label: "Tasks removed from a quote", area: "quotes" },
  quote_tasks_merged: { label: "Quote tasks merged", area: "quotes" },
  quote_task_statuses_changed: {
    label: "Quote task status changed",
    area: "quotes",
  },
  quote_tasks_reviewed: { label: "Quote tasks reviewed", area: "quotes" },
  quote_disposition_changed: { label: "Quote status changed", area: "quotes" },
  quote_customer_opened: { label: "Customer opened a quote", area: "quotes" },
  quote_report_generated: { label: "Quote report generated", area: "quotes" },
  quote_email_failed: { label: "Quote email failed", area: "quotes" },
  atl_page_drafted: { label: "Tech log page drafted", area: "tech_logs" },
  atl_page_flagged: { label: "Tech log page flagged", area: "tech_logs" },
  atl_page_recorded: { label: "Tech log page recorded", area: "tech_logs" },
  atl_page_voided: { label: "Tech log page voided", area: "tech_logs" },
  atl_deferral_raised: { label: "Deferral raised", area: "tech_logs" },
  atl_deferral_cleared: { label: "Deferral cleared", area: "tech_logs" },
  atl_deferral_due_soon: { label: "Deferral due soon", area: "tech_logs" },
  atl_deferral_overdue: { label: "Deferral overdue", area: "tech_logs" },
  tool_issued: { label: "Tool issued", area: "tools" },
  tool_returned: { label: "Tool returned", area: "tools" },
  tool_issue_undone: { label: "Tool issue undone", area: "tools" },
  tool_loan_overdue: { label: "Tool loan overdue", area: "tools" },
  tool_sent_for_calibration: {
    label: "Tool sent for calibration",
    area: "tools",
  },
  tool_calibrated: { label: "Tool calibrated", area: "tools" },
  tool_calibration_due_soon: {
    label: "Tool calibration due soon",
    area: "tools",
  },
  tool_calibration_overdue: {
    label: "Tool calibration overdue",
    area: "tools",
  },
  tool_condition_changed: { label: "Tool condition changed", area: "tools" },
  tool_retired: { label: "Tool retired", area: "tools" },
  tool_restored: { label: "Tool restored", area: "tools" },
  document_processed: { label: "Document processed", area: "documents" },
  email_received: { label: "Email received", area: "emails" },
  email_shared: { label: "Email shared", area: "emails" },
}
